import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const createFleet = createAsyncThunk(
  'fleet/createFleet',
  async (fleetData, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.createFleet, {}, 'POST', fleetData);

      if (response?.data?.success) {
        return {
          data: response?.data?.data,
          message: response?.data?.message || 'Vehicle created successfully'
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to create vehicle');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create vehicle');
    }
  }
);

export const updateFleet = createAsyncThunk(
  'fleet/updateFleet',
  async ({ id, fleetData }, { rejectWithValue }) => {
    try {
      const response = await callAPI(`${apiUrls.updateFleet}/${id}`, {}, 'PATCH', fleetData);

      if (response?.data?.success) {
        return {
          data: response?.data?.data,
          message: response?.data?.message || 'Vehicle updated successfully'
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to update vehicle');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update vehicle');
    }
  }
);

export const deleteFleet = createAsyncThunk(
  'fleet/deleteFleet',
  async (id, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.deleteFleet, { _id: id }, 'DELETE', {});

      if (response?.data?.success) {
        return {
          id,
          message: response?.data?.message || 'Vehicle deleted successfully'
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to delete vehicle');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete vehicle');
    }
  }
);

export const updateFleetStatus = createAsyncThunk(
  'fleet/updateFleetStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.fleetStatusUpdate, { _id: id, status: status }, 'PATCH', { status });

      if (response?.data?.success) {
        return {
          id,
          status,
          message: response?.data?.message || `Status changed to ${status.replace('_', ' ').toUpperCase()}`
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to update vehicle status');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update vehicle status');
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  successMessage: null,
};

const fleetSlice = createSlice({
  name: 'fleet',
  initialState,
  reducers: {
    clearFleetError: (state) => {
      state.error = null;
    },
    clearFleetSuccess: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createFleet.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createFleet.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || 'Vehicle created successfully';
        state.error = null;
      })
      .addCase(createFleet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateFleet.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateFleet.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || 'Vehicle updated successfully';
        state.error = null;
      })
      .addCase(updateFleet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteFleet.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteFleet.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || 'Vehicle deleted successfully';
        state.error = null;
      })
      .addCase(deleteFleet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateFleetStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateFleetStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || `Status changed successfully`;
        state.error = null;
      })
      .addCase(updateFleetStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearFleetError, clearFleetSuccess } = fleetSlice.actions;
export default fleetSlice.reducer;